import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Car, Calendar, Phone, Mail, Home } from 'lucide-react';

const InquirySuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get('type') || 'inquiry';

  useEffect(() => {
    // Redirect to home after 10 seconds
    const timer = setTimeout(() => {
      navigate('/');
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [navigate]);

  const getContent = () => {
    if (type === 'car') {
      return {
        title: 'Car Rental Request Received!',
        message: 'Thank you for your interest in renting a vehicle from HYPA MOTORS.',
        details: 'One of our representatives will contact you within 24 hours to confirm availability and discuss your rental needs.',
        icon: Car
      };
    } else if (type === 'tour') {
      return {
        title: 'Tour Booking Request Received!',
        message: 'Thank you for choosing HYPA MOTORS for your tour experience in Rwanda.',
        details: 'Our tour specialist will get back to you within 24 hours to confirm your booking and provide more details.',
        icon: Calendar
      };
    } else {
      return {
        title: 'Message Sent Successfully!',
        message: 'Thank you for contacting HYPA MOTORS.',
        details: 'We have received your message and will respond within 24 hours.',
        icon: Mail
      };
    }
  };

  const content = getContent();
  const Icon = content.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Success Header */}
          <div className="bg-green-500 p-6 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <CheckCircle className="h-20 w-20 text-white mx-auto" />
            </motion.div>
          </div>

          {/* Content */}
          <div className="p-8 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-bold text-hypa-primary mb-4"
            >
              {content.title}
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex justify-center mb-6"
            >
              <div className="bg-hypa-gold bg-opacity-10 p-4 rounded-full">
                <Icon className="h-12 w-12 text-hypa-gold" />
              </div>
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-gray-700 text-lg mb-4"
            >
              {content.message}
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-gray-600 mb-8"
            >
              {content.details}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-gray-50 rounded-lg p-4 mb-8"
            >
              <h3 className="font-semibold text-hypa-primary mb-2">What happens next?</h3>
              <div className="text-left space-y-2 text-sm text-gray-600">
                <p>✓ Our team will review your request</p>
                <p>✓ You'll receive a confirmation call/email within 24 hours</p>
                <p>✓ We'll discuss payment and pickup details</p>
                <p>✓ Get ready for your luxury experience with HYPA MOTORS!</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/"
                className="inline-flex items-center justify-center space-x-2 bg-hypa-primary text-white px-6 py-3 rounded-lg hover:bg-hypa-dark transition"
              >
                <Home size={18} />
                <span>Back to Home</span>
              </Link>
              <Link
                to="/cars"
                className="inline-flex items-center justify-center space-x-2 border-2 border-hypa-primary text-hypa-primary px-6 py-3 rounded-lg hover:bg-hypa-primary hover:text-white transition"
              >
                <Car size={18} />
                <span>Browse More Cars</span>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center space-x-2 border-2 border-gray-300 text-gray-600 px-6 py-3 rounded-lg hover:border-hypa-primary hover:text-hypa-primary transition"
              >
                <Phone size={18} />
                <span>Contact Support</span>
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="text-xs text-gray-400 mt-8"
            >
              You will be automatically redirected to home page in 10 seconds.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InquirySuccess;